import { useLingui } from '@lingui/react/macro';
import { CircleAlert } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import { useController, useFormContext, type FieldValues, type Path } from 'react-hook-form';

import { Checkbox } from '@/shared/ui/checkbox';

// Escolha múltipla como lista de caixas (o `SelectFormField multiple` do legado). Veio de
// `credit-note/screens/bar-fields` (pendência de shared: "a multisseleção de shared/form ainda não existe").

export interface CheckOption {
  readonly value: string;
  readonly label: string;
  readonly description?: string | undefined;
}

export interface CheckboxListFormFieldProps<V extends FieldValues> {
  readonly name: Path<V>;
  readonly label: ReactNode;
  readonly options: readonly CheckOption[];
  readonly required?: boolean;
  readonly hint?: ReactNode;
  /** Texto quando não há opção; o padrão é "Nenhuma opção disponível.". */
  readonly empty?: ReactNode;
}

/** Escolha múltipla pelo valor, na ordem do clique; o valor do campo é `string[]`. */
export function CheckboxListFormField<V extends FieldValues>({ name, label, options, required, hint, empty }: CheckboxListFormFieldProps<V>) {
  const { t } = useLingui();
  const id = useId();
  const { control } = useFormContext<V>();
  const { field, fieldState } = useController({ name, control });
  const value: string[] = Array.isArray(field.value) ? (field.value as string[]) : [];
  const error = fieldState.error?.message;
  const errorId = error ? `${id}-error` : undefined;
  const hintId = hint ? `${id}-hint` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  const toggle = (option: string, checked: boolean) => field.onChange(checked ? [...value, option] : value.filter((item) => item !== option));
  return (
    <fieldset data-slot="form-field" data-invalid={error ? true : undefined} aria-describedby={describedBy} className="flex min-w-0 flex-col gap-2">
      <legend className="mb-1.5 text-sm font-medium text-foreground">
        {label}
        {required ? (
          <>
            <span aria-hidden className="ml-0.5 text-err">
              *
            </span>
            <span className="sr-only">{t`(obrigatório)`}</span>
          </>
        ) : null}
      </legend>
      {options.length === 0 ? <p className="text-sm text-muted-foreground">{empty ?? t`Nenhuma opção disponível.`}</p> : null}
      {options.map((option) => {
        const optionId = `${id}-${option.value}`;
        return (
          <div key={option.value} className="flex items-start gap-2.5 rounded-md border border-border px-3 py-2.5">
            <Checkbox
              id={optionId}
              ref={value.length === 0 && option === options[0] ? field.ref : undefined}
              checked={value.includes(option.value)}
              onCheckedChange={(checked) => toggle(option.value, checked === true)}
              onBlur={field.onBlur}
              {...(error ? { 'aria-invalid': true } : {})}
              className="mt-0.5"
            />
            <label htmlFor={optionId} className="flex min-w-0 cursor-pointer flex-col gap-0.5 text-sm">
              <span className="font-medium text-foreground">{option.label}</span>
              {option.description ? <span className="text-muted-foreground">{option.description}</span> : null}
            </label>
          </div>
        );
      })}
      {error ? (
        <p id={errorId} className="flex items-center gap-1 text-xs text-err">
          <CircleAlert aria-hidden className="size-3.5 shrink-0" />
          {error}
        </p>
      ) : null}
      {hint ? (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </fieldset>
  );
}
